import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Icon, Button } from '../../components/ui';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';

export default function AccountingHome() {
  const { accountingEnabled } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [lastEntries, setLastEntries] = useState([]);
  const [classTotals, setClassTotals] = useState([]);

  useEffect(() => {
    if (accountingEnabled) loadDashboard();
  }, [accountingEnabled]);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/accounting/dashboard');
      if (res.data?.ok) {
        setStats(res.data.stats);
        setLastEntries(res.data.lastEntries || []);
        setClassTotals(res.data.classTotals || []);
      }
    } catch {}
  };

  if (!accountingEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <Card>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Comptabilité</h1>
            <p className="text-gray-600">Module désactivé pour ce dossier.</p>
          </Card>
        </div>
      </div>
    );
  }

  const formatCurrency = (v) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(v || 0);

  const quickLinks = [
    { path: '/accounting/entries', label: 'Écritures', icon: 'IconBook', desc: 'Poster ventes, achats, trésorerie', color: 'bg-blue-500' },
    { path: '/accounting/manual-entry', label: 'Saisie manuelle', icon: 'IconPencil', desc: 'Saisir une écriture manuelle', color: 'bg-green-500' },
    { path: '/accounting/journal', label: 'Journal', icon: 'IconBookOpen', desc: 'Consulter le journal', color: 'bg-purple-500' },
    { path: '/accounting/general-ledger', label: 'Grand Livre', icon: 'IconList', desc: 'Grand Livre par compte', color: 'bg-orange-500' },
    { path: '/accounting/trial-balance', label: 'Balance', icon: 'IconScale', desc: 'Balance des comptes', color: 'bg-teal-500' },
    { path: '/accounting/financial-statements', label: 'États Financiers', icon: 'IconReportAnalytics', desc: 'Bilan, CPC', color: 'bg-red-500' },
    { path: '/accounting/chart-of-accounts', label: 'Plan Comptable', icon: 'IconSitemap', desc: 'Gérer le plan de comptes', color: 'bg-indigo-500' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconScale" className="text-indigo-600 mr-3" />
          Tableau de bord comptable
        </h1>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="text-center">
            <div className="text-3xl font-bold text-indigo-600">{stats.totalEntries}</div>
            <div className="text-sm text-gray-600">Total écritures</div>
          </Card>
          <Card className="text-center">
            <div className="text-3xl font-bold text-green-600">{stats.postedEntries}</div>
            <div className="text-sm text-gray-600">Postées</div>
          </Card>
          <Card className="text-center">
            <div className="text-3xl font-bold text-orange-600">{stats.draftEntries}</div>
            <div className="text-sm text-gray-600">Brouillons</div>
          </Card>
          <Card className="text-center">
            <div className="text-3xl font-bold text-blue-600">{stats.totalEntries}</div>
            <div className="text-sm text-gray-600">Périodes comptables</div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {quickLinks.map((link) => (
          <Card
            key={link.path}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(link.path)}
          >
            <div className="flex items-start gap-3">
              <div className={`p-3 rounded-lg ${link.color}`}>
                <Icon name={link.icon} className="text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-800">{link.label}</div>
                <div className="text-xs text-gray-600">{link.desc}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-bold text-gray-800 mb-4">Dernières écritures</h2>
          {lastEntries.length === 0 ? (
            <div className="text-gray-500 text-sm">Aucune écriture récente</div>
          ) : (
            <div className="space-y-2">
              {lastEntries.map((e) => (
                <div key={e._id} className="flex justify-between items-center py-2 border-b text-sm">
                  <div>
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{e.journalCode}-{e.entryNumber}</span>
                    <span className="ml-2">{e.label}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {e.entryDate ? new Date(e.entryDate).toLocaleDateString('fr-FR') : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 className="text-lg font-bold text-gray-800 mb-4">Totaux par classe</h2>
          {classTotals.length === 0 ? (
            <div className="text-gray-500 text-sm">Aucune écriture</div>
          ) : (
            <div className="space-y-2">
              {classTotals.map((c) => (
                <div key={c._id} className="flex justify-between py-2 border-b text-sm">
                  <span>Classe {c._id}</span>
                  <div>
                    <span className="mr-4">D: {formatCurrency(c.totalDebit)}</span>
                    <span>C: {formatCurrency(c.totalCredit)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
